"use client";

import * as React from "react";
import { ArrowRight, BookOpen, Clock, ShieldCheck, UserCheck } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
const fallbackQuestions = [{
  id: "cat-q-1",
  title: "Why is my cat not eating?",
  excerpt: "A sudden loss of appetite in cats can signal dental discomfort, gastrointestinal upset, or environmental stress. Explore when dietary pauses require immediate veterinary intervention.",
  link: "/PetHealthPage",
  featuredImage: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/cat_not_eating_clinical_guide.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/cat_not_eating_clinical_guide.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/cat_not_eating_clinical_guide.png-wvc-sizes",
  authorName: "Dr. Eleanor Vance, DVM",
  readTime: "4 min read",
  topic: "Feline Nutrition & Health"
}, {
  id: "cat-q-2",
  title: "Understanding cat body language and tail signals",
  excerpt: "From subtle ear twitches to slow blinks and tail positions, learn how cats communicate comfort, tension, and underlying pain cues throughout the home.",
  link: "/BehaviorTrainingPage",
  featuredImage: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/cat_body_language_behavior_guide.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/cat_body_language_behavior_guide.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/cat_body_language_behavior_guide.png-wvc-sizes",
  authorName: "Dr. Marcus Chen, BVSc",
  readTime: "6 min read",
  topic: "Feline Behavior"
}, {
  id: "cat-q-3",
  title: "How often should adult indoor cats visit the vet?",
  excerpt: "Preventive wellness examinations detect chronic kidney issues, hypertension, and metabolic shifts early. Review recommended screening timelines by age group.",
  link: "/PetHealthPage",
  featuredImage: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/cat_veterinary_checkup_examination.png",
  srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/cat_veterinary_checkup_examination.png-wvc-srcset",
  sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/cat_veterinary_checkup_examination.png-wvc-sizes",
  authorName: "Dr. Sarah Jenkins, DACVIM",
  readTime: "5 min read",
  topic: "Preventive Medicine"
}];
function QuestionsConsumer() {
  const {
    posts,
    loading,
    error,
    refetch
  } = usePosts();
  if (loading) {
    return <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
        {[0, 1, 2].map(placeholderIndex => <div key={placeholderIndex} data-index={placeholderIndex} className="flex flex-col gap-4 rounded-xl border border-border bg-card p-6">
            <div className="h-48 w-full animate-pulse rounded-lg bg-muted" />
            <div className="h-4 w-1/3 animate-pulse rounded bg-muted" />
            <div className="h-6 w-4/5 animate-pulse rounded bg-muted" />
            <div className="h-16 w-full animate-pulse rounded bg-muted" />
            <div className="mt-auto h-8 w-1/2 animate-pulse rounded bg-muted" />
          </div>)}
      </div>;
  }
  if (error) {
    return <div className="rounded-xl border border-border bg-card p-8 text-center">
        <p className="text-sm text-muted-foreground">
          Unable to load recent veterinary articles at this moment.
        </p>
        <Button variant="outline" size="sm" onClick={() => refetch()} className="mt-4">
          Retry
        </Button>
      </div>;
  }
  const displayList = posts && posts.length > 0 ? posts.slice(0, 3).map((post, index) => {
    const fallback = fallbackQuestions[index % fallbackQuestions.length];
    return {
      id: post.id || `post-${index}`,
      title: post.title || fallback.title,
      excerpt: post.excerpt?.replace(/<[^>]*>?/gm, "") || fallback.excerpt,
      link: post.link || fallback.link,
      featuredImage: post.featuredImage || fallback.featuredImage,
      authorName: post.author?.name || fallback.authorName,
      readTime: fallback.readTime,
      topic: post.categories && post.categories.length > 0 ? post.categories[0].name : fallback.topic
    };
  }) : fallbackQuestions;
  return <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
      {displayList.map((item, itemIndex) => <Card key={item.id} data-index={itemIndex} className="group flex flex-col justify-between overflow-hidden rounded-xl border border-border bg-card transition-[border-color,box-shadow,transform] duration-200 hover:-translate-y-1 hover:border-border/80 hover:shadow-md">
          <div>
            <div className="relative aspect-[16/10] w-full overflow-hidden bg-muted">
              <img src={item.featuredImage} alt={item.title} loading="lazy" decoding="async" data-wvc-srcset={"srcset" in item ? (item as typeof fallbackQuestions[number]).srcset : undefined} data-wvc-sizes={"sizes" in item ? (item as typeof fallbackQuestions[number]).sizes : undefined} className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.02]" />
              <div className="absolute top-3 left-3">
                <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-background/95 px-2.5 py-1 text-xs font-semibold text-foreground backdrop-blur-xs">
                  <ShieldCheck className="h-3.5 w-3.5 text-primary" />
                  Veterinary reviewed
                </span>
              </div>
            </div>

            <CardHeader className="space-y-2 p-5 pb-2">
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span className="font-semibold uppercase tracking-wider text-foreground/80">
                  {item.topic}
                </span>
                <span className="inline-flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  {item.readTime}
                </span>
              </div>

              <CardTitle className="font-serif text-xl font-semibold leading-snug tracking-tight text-card-foreground group-hover:text-primary">
                <Link to={item.link || "#"} className="hover:underline">
                  {item.title}
                </Link>
              </CardTitle>
            </CardHeader>

            <CardContent className="px-5 py-2">
              <p className="line-clamp-3 text-sm leading-relaxed text-muted-foreground">
                {item.excerpt}
              </p>
            </CardContent>
          </div>

          <CardFooter className="flex flex-col items-start gap-4 border-t border-border/60 bg-background/50 p-5">
            <div className="flex w-full items-center justify-between text-xs">
              <div className="inline-flex items-center gap-1.5 font-medium text-foreground">
                <UserCheck className="h-3.5 w-3.5 text-muted-foreground" />
                <span>{item.authorName}</span>
              </div>
            </div>

            <div className="w-full">
              <Button asChild variant="outline" size="sm" className="w-full justify-between font-semibold group-hover:border-primary/40 group-hover:bg-accent group-hover:text-accent-foreground">
                <Link to={item.link || "#"}>
                  <span>Read full answer</span>
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </Link>
              </Button>
            </div>
          </CardFooter>
        </Card>)}
    </div>;
}
export default function CatQuestions() {
  const wp_query_params = {
    post_type: "post",
    per_page: 3,
    order: "desc",
    orderby: "date",
    search: "cat"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section id="popular-cat-questions" data-nav="light" className="bg-background py-16 text-foreground md:py-20 lg:py-24">
      <div className="container mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="mb-12 flex flex-col justify-between gap-6 border-b border-border pb-8 md:flex-row md:items-end">
          <div className="max-w-2xl space-y-3">
            <div className="inline-flex items-center gap-2 rounded-md bg-secondary px-2.5 py-1 text-xs font-semibold text-secondary-foreground">
              <BookOpen className="h-3.5 w-3.5" />
              <span>Clinical Feline Reference</span>
            </div>
            <h2 className="font-serif text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">
              Answers to common cat questions
            </h2>
            <p className="text-base text-muted-foreground sm:text-lg">
              Clear explanations for everyday feline habits, dietary changes, and health symptoms, reviewed by practicing veterinarians.
            </p>
          </div>

          <div className="shrink-0">
            <Button asChild variant="outline" className="font-semibold">
              <Link to="/PetHealthPage">
                <span>Browse all cat health topics</span>
                <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>
          </div>
        </div>

        {/* Clinical Governance Callout */}
        <div className="mb-10 rounded-lg border border-border border-l-4 border-l-primary bg-card p-4 sm:p-5">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex items-start gap-3">
              <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
              <div>
                <p className="text-sm font-semibold text-card-foreground">
                  Our clinical review standard
                </p>
                <p className="text-xs text-muted-foreground">
                  Every guidance card is validated against current veterinary consensus and diagnostic protocols.
                </p>
              </div>
            </div>
            <Link to="/AboutPetsWhirlPage" className="text-xs font-semibold text-primary underline-offset-4 hover:underline sm:self-center">
              How we review content
            </Link>
          </div>
        </div>

        {/* Dynamic Posts Consumer */}
        <PostsProvider wp_query={{
        post_type: "post",
        per_page: 3,
        order: "desc",
        orderby: "date",
        search: "cat",
        status: ["publish"]
      }}>
          <QuestionsConsumer />
        </PostsProvider>
      </div>
    </section>;
}